/**
 * STUNDENKONTINGENT IN DER KOPIERKETTE (S1) — reine Logik, kein Schreibpfad.
 *
 * Das Kontingent einer Paketposition („Planungsphase = 100 h") hat seine
 * Vorgabe im Leistungskatalog (`service_catalog.stunden_kontingent`, Nr. 171)
 * und wird beim ANLEGEN kopiert: Katalog → Projektanlage / Nachtrag /
 * Angebot, LV-Vorlage → Projekt, Angebot → Auftrag und Teilbeauftragung.
 * Auftrag und Entscheide: docs/stundenkontingent-vertrag.md.
 *
 * Die eine Regel, die hier steht (S1): das Kontingent reist NUR zusammen mit
 * den Bestandteilen. Massgeblich ist die Liste, die in der KOPIE ankommt, nicht
 * die der Quelle — faellt sie unterwegs weg (Menge ≠ 1 bei der Auftragsannahme,
 * Monatspauschale, kaputte Vorlage), faellt das Kontingent mit. Ein Kontingent
 * an einer gewoehnlichen Position waere ein Wert, den keine Oberflaeche zeigt
 * und den der Trigger aus Nr. 172 danach nie mehr aendern laesst (S10).
 *
 * Ein ungueltiger Wert reist nie mit: er faellt weg, statt die Kopie (oder
 * spaeter das Speichern am CHECK aus Nr. 171) scheitern zu lassen.
 *
 * Pure Funktionen, kein Supabase, kein React — in Node testbar und auch aus
 * den Diensten (quoteLogic, serviceItemDraft) nutzbar.
 */
import type { PositionsBestandteil } from '../../types';
import { hatBestandteile } from './bestandteilLogic';
import { KONTINGENT_MAX, stundenAusDb } from './stundenLogic';

/**
 * Ein Kontingent, wie es der CHECK aus Nr. 171 annimmt (0 < h ≤ 99999) —
 * dieselbe Grenze wie `projectLv.stundenKontingentZeile` —, sonst undefined.
 * Tolerant gegen Text („100.00" aus numeric, „100" aus einer Vorlage in
 * jsonb), nie NaN.
 */
export const gueltigesKontingent = (wert: unknown): number | undefined => {
    const h = stundenAusDb(wert);
    if (h === null) return undefined;
    // Erst auf numeric(9,2) runden, dann pruefen: ein 0,004 aus einer fremden
    // Vorlage wuerde in der Datenbank 0,00 und kippte am CHECK (> 0) das
    // ganze Speichern (projectLv.gueltigesKontingentWert, gleiche Regel).
    const gerundet = Math.round(h * 100) / 100;
    return gerundet > 0 && gerundet <= KONTINGENT_MAX ? gerundet : undefined;
};

/**
 * S1 — was eine Kopie an Kontingent mitnimmt: nur, wenn die KOPIE
 * Bestandteile hat, und nur einen gueltigen Wert. Sonst kein Schluessel —
 * ein ausgeschriebenes `stundenKontingent: undefined` braucht niemand
 * (Muster `bestandteile`).
 *
 * Zum Aufspreizen gedacht: `{ ...kopie, ...kontingentFuerKopie(kopie, quelle.stundenKontingent) }`.
 */
export const kontingentFuerKopie = (
    kopie: { bestandteile?: PositionsBestandteil[] | null } | null | undefined,
    wert: unknown,
): { stundenKontingent?: number } => {
    if (!hatBestandteile(kopie)) return {};
    const h = gueltigesKontingent(wert);
    return h === undefined ? {} : { stundenKontingent: h };
};
